/**
 * LWI-1150 — authorization audit trail for one PO line (the Audit tab on
 * the Edit PO page). Reads GET purchase-orders/lines/{pid}/audit
 * (audit_log events from LWI-1137/LWI-1138 plus the bulk events), which
 * is gated by lcs_po.audit.view — the parent only renders this component
 * for holders of that permission.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import apiClient from "@utils/axiosInstance";
import { shadow } from "../../../design-system/tokens";

dayjs.extend(utc);

// audit_log rows are stamped in UTC (app.timezone); render viewer-local.
const formatLocalTime = (raw) =>
  raw ? dayjs.utc(raw).local().format("YYYY-MM-DD HH:mm:ss") : "—";

// Event + relation → chip label/color and a human-readable detail line.
export const describeEvent = (row) => {
  const meta = row.metadata || {};
  // LWI-1152 — the BE resolves bulk pids to PO composites; fall back to the
  // internal pid only when the referenced line no longer exists.
  const sourceRef = row.source_po_id ? `PO ${row.source_po_id}` : `line ${meta.source_pid}`;
  const targetRef = row.target_po_id ? `PO ${row.target_po_id}` : `line ${meta.target_pid}`;
  // LWI-1513 follow-up — a bulk event is written once and read from both
  // ends, so every row names THIS line explicitly. "Applied to PO …,1,3,1"
  // on its own read as "only 1,3,1" to the admin who authorized 2,2,1.
  const hereRef =
    (row.relation === "source" && row.source_po_id) ||
    (row.relation === "target" && row.target_po_id) ||
    meta.po_id ||
    null;
  const here = hereRef ? `this line (PO ${hereRef})` : "this line";
  const Here = here.charAt(0).toUpperCase() + here.slice(1);
  switch (row.event_type) {
    case "po_line_authorized":
      return { label: "Authorized to ship", color: "success", detail: `${Here} was authorized to ship` };
    case "po_line_auto_authorized":
      return {
        label: "Auto-authorized",
        color: "success",
        detail: `Supplier answered "No US content" — ${here} was authorized automatically`,
      };
    case "po_line_authorization_reset":
      return {
        label: "Authorization reset",
        color: "warning",
        detail:
          meta.previous_authorized_to_ship != null
            ? `Was authorized to ship: ${Number(meta.previous_authorized_to_ship) === 1 ? "Yes" : "No"}`
            : "",
      };
    case "po_line_authorized_bulk":
      return row.relation === "source"
        ? {
            label: "Authorized, also applied to other lines",
            color: "success",
            detail: `${Here} was authorized to ship; the authorization was also applied to ${targetRef}`,
          }
        : {
            label: "Authorized via bulk apply",
            color: "success",
            detail: `${Here} was authorized to ship by copying the authorization from ${sourceRef}`,
          };
    case "po_line_us_content_bulk_applied":
      return row.relation === "source"
        ? {
            label: "US content applied to other lines",
            color: "default",
            detail: `The US-content answers on ${here} were also applied to ${targetRef}`,
          }
        : {
            label: "US content via bulk apply",
            color: "default",
            detail: `${Here} received its US-content answers copied from ${sourceRef}`,
          };
    case "po_line_updated": {
      // LWI-1513 — field-level edits. `changes` is [{field, label, from, to}];
      // has_us_content travels as "1"/"0", everything else as text.
      const changes = Array.isArray(meta.changes) ? meta.changes : [];
      const show = (c, v) => {
        if (v == null || v === "") return "—";
        if (c.field === "has_us_content") return v === "1" ? "Yes" : "No";
        return v;
      };
      const onlyUsContent = changes.length === 1 && changes[0].field === "has_us_content";
      return {
        label: onlyUsContent ? "US content answered" : "Line edited",
        color: onlyUsContent ? "info" : "default",
        detail: (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {changes.map((c) => (
              <li key={c.field}>
                {c.label}: {show(c, c.from)} → {show(c, c.to)}
              </li>
            ))}
          </Box>
        ),
      };
    }
    default:
      return { label: row.event_type, color: "default", detail: "" };
  }
};

function PoLineAuditTable({ pid, lineRef }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [perPage, setPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get(
        `purchase-orders/lines/${pid}/audit`,
        { params: { page: page + 1, per_page: perPage } }
      );
      setRows(response.data?.data || []);
      setTotal(response.data?.meta?.total || 0);
    } catch (e) {
      console.error("Error fetching PO line audit:", e.response?.data || e.message);
      setError("Failed to load audit history.");
    } finally {
      setLoading(false);
    }
  }, [pid, page, perPage]);

  useEffect(() => {
    if (pid) load();
  }, [pid, load]);

  return (
    <Box
      data-testid="po-line-audit-panel"
      sx={{
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: shadow.panel,
        p: 4,
      }}
    >
      <Typography variant="h5" mb={lineRef ? 0.5 : 2}>
        Line Audit
      </Typography>
      {lineRef && (
        <Typography variant="body2" color="text.secondary" mb={2} data-testid="po-line-audit-ref">
          PO {lineRef} — every event below happened on this line; bulk rows also name the other line involved.
        </Typography>
      )}
      {error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small" data-testid="po-line-audit-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 200 }}>Time</TableCell>
                  <TableCell sx={{ width: 260 }}>Event</TableCell>
                  <TableCell sx={{ width: 260 }}>Actor</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const ev = describeEvent(row);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatLocalTime(row.created_at)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={ev.label} color={ev.color} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {row.actor_name || row.actor_email || "—"}
                        {row.actor_name && row.actor_email && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                            {row.actor_email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{ev.detail}</TableCell>
                    </TableRow>
                  );
                })}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No authorization audit events recorded for this line.
                        Authorization history is collected from July 2026 onward; field edits from September 2026.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </>
      )}
    </Box>
  );
}

export default PoLineAuditTable;
